function docSubServiceSlug(doc) {
  if (!doc || typeof doc !== 'object') return '';
  return (
    (typeof doc.targetSubServiceType === 'string' && doc.targetSubServiceType.trim()) ||
    (typeof doc.subServiceType === 'string' && doc.subServiceType.trim()) ||
    ''
  );
}

function getLeadSummaries(doc) {
  return doc?.leads || [];
}

function hasLeadToBusiness(doc, businessId) {
  return getLeadSummaries(doc).some((entry) => entry?.businessId === businessId);
}

function findActiveWizardSearchDoc(requests, wizardSearchRequestId) {
  if (!wizardSearchRequestId || !Array.isArray(requests)) return null;
  const id = String(wizardSearchRequestId).trim();
  if (!id) return null;
  return requests.find((doc) => doc?.id === id) ?? null;
}

/**
 * Whether this client already sent a custom inquiry for this offering to this business
 * (any prior custom search request document — once per client per business + subservice).
 * @param {Array<object> | null | undefined} requests
 * @param {{ businessId?: string, subServiceType?: string }} target
 */
export function isCustomInquiryAlreadySent(requests, { businessId, subServiceType }) {
  if (!businessId || !subServiceType || !Array.isArray(requests)) return false;
  const slug = String(subServiceType).trim();
  if (!slug) return false;

  return requests.some((doc) => {
    if (doc?.inquiryKind !== 'custom') return false;
    if (docSubServiceSlug(doc) !== slug) return false;
    return hasLeadToBusiness(doc, businessId);
  });
}

/**
 * Whether the viewer already sent a wizard (catalog) inquiry for this offering to this business
 * on the active service wizard search document only.
 * @param {Array<object> | null | undefined} requests
 * @param {{ businessId?: string, serviceType?: string, subServiceType?: string, wizardSearchRequestId?: string }} target
 */
export function isCatalogInquiryAlreadySent(
  requests,
  { businessId, serviceType, subServiceType, wizardSearchRequestId },
) {
  if (!businessId || !serviceType || !subServiceType || !wizardSearchRequestId) {
    return false;
  }
  const trade = String(serviceType).trim();
  const sub = String(subServiceType).trim();
  if (!trade || !sub) return false;

  const doc = findActiveWizardSearchDoc(requests, wizardSearchRequestId);
  if (!doc || doc.inquiryKind === 'custom') return false;
  if (String(doc.serviceType || '').trim() !== trade) return false;
  if (docSubServiceSlug(doc) !== sub) return false;
  return hasLeadToBusiness(doc, businessId);
}

/**
 * Storefront service card — custom or catalog subservices from business listing.
 * @param {Array<object> | null | undefined} requests
 * @param {{ businessId?: string, service?: object, wizardSearchRequestId?: string }} target
 */
export function isStorefrontInquiryAlreadySent(
  requests,
  { businessId, service, wizardSearchRequestId },
) {
  if (!businessId || !service) return false;

  const serviceType = service._parentServiceType || service.ServiceType;
  const subServiceType = service.SubServiceType;

  if (service.IsCustom) {
    return isCustomInquiryAlreadySent(requests, { businessId, subServiceType });
  }

  return isCatalogInquiryAlreadySent(requests, {
    businessId,
    serviceType,
    subServiceType,
    wizardSearchRequestId,
  });
}

/**
 * Set of SubServiceType slugs this client already inquired on at this storefront (custom only).
 * @param {Array<object> | null | undefined} requests
 * @param {string | undefined} businessId
 * @returns {Set<string>}
 */
export function buildSentCustomInquirySlugSet(requests, businessId) {
  const sent = new Set();
  if (!businessId || !Array.isArray(requests)) return sent;

  for (const doc of requests) {
    if (doc?.inquiryKind !== 'custom') continue;
    const slug = docSubServiceSlug(doc);
    if (!slug) continue;
    if (hasLeadToBusiness(doc, businessId)) sent.add(slug);
  }
  return sent;
}
